import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, Checkbox, FormControlLabel, LinearProgress, makeStyles, Tooltip, Typography} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import DataTable from "react-data-table-component";
import {Button, GeoZoneMap, GeoZoneMapPointsLayer, HeadBarProps, HeatmapLayer, Option, VectorTileLayerConfig} from "components";
import {OrganizationRef} from "api/organization";
import {User} from "api/organization/users";
import {geoZoneClient, Territory} from "api/geoZone";
import {buildTourApiUrl, deliveryApiUrl} from "api/configuration";
import {keycloakClient} from "api/keyclaok";
import {
    deliveryCsvAnalysisClient,
    DeliveryCsvAnalysisDetail,
    DeliveryCsvAnalysisQueryResult,
    DeliveryCsvAnalysisQueryStatus,
    DeliveryCsvAnalysisSummary,
    DeliveryCsvBonusOverview,
    DeliveryCsvLineError,
    HeatmapPoint,
    HeatMatricesData,
    ReanalysisJobStatus,
    ReanalysisProgress,
} from "api/deliveryCsvAnalysis";
import withConnect from "./withConnect";
import {AnalysisFilters, AnalysisFiltersModel} from "./components/AnalysisFilters";
import {AnalysisSummaryCards} from "./components/AnalysisSummaryCards";
import {RunningJobs} from "./components/RunningJobs";
import {AnalysisResultsTable} from "./components/AnalysisResultsTable";
import {BonusOverviewTable} from "./components/BonusOverviewTable";
import {
  HeatMatrix,
  useDayOfWeekCols,
  useHourRows,
  useMonthCols,
  useYearMonthRows,
} from "app/components/HeatMatrix";

const useStyles = makeStyles(() => ({
  container: {
    padding: "30px",
    maxWidth: "1800px",
  },
  section: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "20px",
    color: "#353945",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: "16px",
  },
  alertBox: {
    padding: "16px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  pendingAlert: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    border: "1px solid #ff9800",
  },
  inProgressAlert: {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    border: "1px solid #2196f3",
  },
  infoAlert: {
    backgroundColor: "rgba(33, 150, 243, 0.08)",
    border: "1px solid #90caf9",
  },
  progressContainer: {
    marginTop: "12px",
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
  },
  button: {
    height: "44px",
  },
  card: {
    overflow: "auto",
  },
  warningText: {
    color: "#F90",
    background: "rgba(255, 153, 0, 0.10)",
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  mapContainer: {
    marginTop: "15px",
  },
  matricesRow: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
}));

// Hook to build territory GeoJSON from bonus overviews (reused pattern from CsvScanner)
const useTerritoriesGeoJson = (bonusOverviews: DeliveryCsvBonusOverview[] | undefined) => {
  const [territoriesData, setTerritoriesData] = useState<Record<string, Record<string, number[][][]>> | undefined>();

  useEffect(() => {
    if (!bonusOverviews || bonusOverviews.length === 0) {
      setTerritoriesData(undefined);
      return;
    }

    const territoryCodes = bonusOverviews
      .map((b) => b.territoryCode)
      .filter((code): code is string => code != null);

    if (territoryCodes.length === 0) {
      setTerritoriesData(undefined);
      return;
    }

    const uniqueCodes = [...new Set(territoryCodes)];
    geoZoneClient.query.getTerritoriesData(uniqueCodes)
      .then((data) => setTerritoriesData(data))
      .catch(() => setTerritoriesData(undefined));
  }, [bonusOverviews]);

  return useMemo(() => {
    if (!territoriesData) return undefined;

    const features: string[] = [];
    Object.values(territoriesData).forEach((geozonesData) => {
      Object.values(geozonesData).forEach((geozoneData) => {
        features.push(`{
          "type": "Feature",
          "geometry": {
            "type": "MultiPolygon",
            "coordinates": [${JSON.stringify(geozoneData)}]
          }
        }`);
      });
    });

    return `{
      "type": "FeatureCollection",
      "features": [${features}]
    }`;
  }, [territoriesData]);
};

// Convert points to GeoJSON for circle markers (single file detail view)
const pointsToGeoJson = (
  points: Array<{ lat: number; lon: number }> | undefined
): string | undefined => {
  if (!points || points.length === 0) return undefined;

  const features = points.map((point) => {
    return `{
      "type": "Feature",
      "geometry": {
        "type": "Point",
        "coordinates": [${point.lon}, ${point.lat}]
      }
    }`;
  });

  return `{
    "type": "FeatureCollection",
    "features": [${features.join(",")}]
  }`;
};

// Convert heatmap points to GeoJSON with weight property for Mapbox heatmap layer
const heatmapPointsToGeoJson = (
  points: HeatmapPoint[] | undefined
): string | undefined => {
  if (!points || points.length === 0) return undefined;

  const features = points.map((point) => {
    return `{
      "type": "Feature",
      "properties": { "weight": ${point.count} },
      "geometry": {
        "type": "Point",
        "coordinates": [${point.lon}, ${point.lat}]
      }
    }`;
  });

  return `{
    "type": "FeatureCollection",
    "features": [${features.join(",")}]
  }`;
};

interface ReceivedDataAnalysisPageProps {
  currentUser: User | null;
  organizationRefs: Map<string, OrganizationRef>;
  carrierRefs: Map<string, OrganizationRef>;
  operatorRefs: Map<string, OrganizationRef>;
  territories: Territory[];
  setHeadBar: (headBar: HeadBarProps) => void;
}

const ReceivedDataAnalysisPage = (props: ReceivedDataAnalysisPageProps) => {
  const { organizationRefs, carrierRefs, operatorRefs, territories, setHeadBar } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [filterValues, setFilterValues] = useState<AnalysisFiltersModel>({
    importerIds: [],
    operatorIds: [],
    carrierIds: [],
    territoryCodes: [],
    successOnly: true,
  });
  const [loading, setLoading] = useState(false);
  const [queryResult, setQueryResult] = useState<DeliveryCsvAnalysisQueryResult | null>(null);
  const [jobs, setJobs] = useState<ReanalysisProgress[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedFileDetail, setSelectedFileDetail] = useState<DeliveryCsvAnalysisDetail | null>(null);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [showHorsZone, setShowHorsZone] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportTriggered, setExportTriggered] = useState(false);

  useEffect(() => {
    setHeadBar({ title: t("received_data_analysis_title") });
  }, [setHeadBar, t]);

  // Load jobs on mount and periodically
  useEffect(() => {
    const fetchJobs = () => {
      deliveryCsvAnalysisClient.getJobs()
        .then((result) => setJobs(result ?? []))
        .catch(() => setJobs([]));
    };
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-load results when our pending job completes
  useEffect(() => {
    if (!pendingJobId) return;

    const pendingJob = jobs.find((job) => job.jobId === pendingJobId);
    if (!pendingJob) return;

    const status = pendingJob.status?.toString();

    // Clear pending if job failed or was cancelled
    if (status === ReanalysisJobStatus.FAILED || status === ReanalysisJobStatus.CANCELLED) {
      setPendingJobId(null);
      return;
    }

    // Auto-load results if completed
    if (status === ReanalysisJobStatus.COMPLETED && pendingJob.query) {
      setPendingJobId(null);
      setLoading(true);
      setSelectedFileId(null);
      setSelectedFileDetail(null);
      deliveryCsvAnalysisClient.queryAnalyses({
        ...pendingJob.query,
        page: 0,
        size: 1000,
      })
        .then((result) => setQueryResult(result))
        .catch((error) => console.error("Error loading job results:", error))
        .finally(() => setLoading(false));
    }
  }, [jobs, pendingJobId]);

  const importerOptions: Option[] = useMemo(() => {
    return Array.from(organizationRefs.values()).map((org) => ({
      label: org.displayName,
      value: org.organizationId,
    }));
  }, [organizationRefs]);

  const operatorOptions: Option[] = useMemo(() => {
    return Array.from(operatorRefs.values()).map((org) => ({
      label: org.displayName,
      value: org.organizationId,
    }));
  }, [operatorRefs]);

  const carrierOptions: Option[] = useMemo(() => {
    return Array.from(carrierRefs.values()).map((org) => ({
      label: org.displayName,
      value: org.organizationId,
    }));
  }, [carrierRefs]);

  const territoryOptions: Option[] = useMemo(() => {
    return territories?.map((t) => ({
      label: t.name,
      value: t.code,
    })) ?? [];
  }, [territories]);

  // Format period from DateInterval to "YYYY-MM" string
  const formatPeriod = (period?: { startDate: number; endDate: number }): string | undefined => {
    if (!period) return undefined;
    const date = new Date(period.startDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const buildQuery = useCallback(() => ({
    uploadPeriodStart: formatPeriod(filterValues.uploadPeriodStart),
    uploadPeriodEnd: formatPeriod(filterValues.uploadPeriodEnd),
    contentPeriodStart: formatPeriod(filterValues.contentPeriodStart),
    contentPeriodEnd: formatPeriod(filterValues.contentPeriodEnd),
    importerIds: filterValues.importerIds,
    operatorIds: filterValues.operatorIds,
    carrierIds: filterValues.carrierIds,
    territoryCodes: filterValues.territoryCodes,
    successOnly: filterValues.successOnly,
    page: 0,
    size: 1000,
  }), [filterValues]);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSelectedFileId(null);
    setSelectedFileDetail(null);
    try {
      const result = await deliveryCsvAnalysisClient.queryAnalyses(buildQuery());
      setQueryResult(result);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const handleTriggerReanalysis = useCallback(async () => {
    setLoading(true);
    try {
      const job = await deliveryCsvAnalysisClient.triggerReanalysis(buildQuery());
      setPendingJobId(job.jobId);
      const updatedJobs = await deliveryCsvAnalysisClient.getJobs();
      setJobs(updatedJobs ?? []);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const handleCancelJob = useCallback(async (jobId: string) => {
    await deliveryCsvAnalysisClient.cancelJob(jobId);
    const updatedJobs = await deliveryCsvAnalysisClient.getJobs();
    setJobs(updatedJobs ?? []);
  }, []);

  const handleJobClick = useCallback(async (job: ReanalysisProgress) => {
    const jobStatus = job.status?.toString();
    if (jobStatus === ReanalysisJobStatus.COMPLETED && job.query) {
      setLoading(true);
      setSelectedFileId(null);
      setSelectedFileDetail(null);
      try {
        const result = await deliveryCsvAnalysisClient.queryAnalyses({
          ...job.query,
          page: 0,
          size: 1000,
        });
        setQueryResult(result);
      } catch (error) {
        console.error("Error loading job results:", error);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const handleFileClick = useCallback(async (file: DeliveryCsvAnalysisSummary) => {
    // Toggle selection - if same file is clicked, deselect
    if (selectedFileId === file.id) {
      setSelectedFileId(null);
      setSelectedFileDetail(null);
      return;
    }

    setSelectedFileId(file.id);
    setSelectedFileDetail(null);
    try {
      const hasContentFilters = !!(
        filterValues.contentPeriodStart ||
        filterValues.contentPeriodEnd ||
        filterValues.operatorIds.length > 0 ||
        filterValues.carrierIds.length > 0 ||
        filterValues.territoryCodes.length > 0
      );
      const contentFilters = hasContentFilters ? {
        operatorIds: filterValues.operatorIds.length > 0 ? filterValues.operatorIds : undefined,
        carrierIds: filterValues.carrierIds.length > 0 ? filterValues.carrierIds : undefined,
        territoryCodes: filterValues.territoryCodes.length > 0 ? filterValues.territoryCodes : undefined,
        contentPeriodStart: formatPeriod(filterValues.contentPeriodStart),
        contentPeriodEnd: formatPeriod(filterValues.contentPeriodEnd),
      } : undefined;
      const detail = await deliveryCsvAnalysisClient.getAnalysisDetail(file.id, contentFilters);
      setSelectedFileDetail(detail);
    } catch (error) {
      console.error("Error loading file detail:", error);
    }
  }, [selectedFileId, filterValues]);

  const handleExportCsv = useCallback(async () => {
    setExportLoading(true);
    setExportTriggered(false);
    try {
      await deliveryCsvAnalysisClient.triggerCsvExport(buildQuery());
      setExportTriggered(true);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  }, [buildQuery]);

  // Compare as strings since backend returns string values
  const statusValue = queryResult?.status?.toString();
  const isReanalysisInProgress = statusValue === DeliveryCsvAnalysisQueryStatus.REANALYSIS_IN_PROGRESS;
  const isPendingReanalysis = statusValue === DeliveryCsvAnalysisQueryStatus.PENDING_REANALYSIS;

  // Check if content filters are active (based on current filter values, not query result)
  const contentFiltersActive = !!(
    filterValues.contentPeriodStart ||
    filterValues.contentPeriodEnd ||
    filterValues.operatorIds.length > 0 ||
    filterValues.carrierIds.length > 0 ||
    filterValues.territoryCodes.length > 0
  );

  const errorColumns = useMemo(() => [
    {
      name: t("app_csv_scanner_error_type"),
      width: "30vw",
      cell: (row: DeliveryCsvLineError) => <p>{row.error}</p>,
    },
    {
      name: t("app_csv_scanner_error_comment"),
      width: "30vw",
      cell: (row: DeliveryCsvLineError) => <p>{row.value}</p>,
    },
    {
      name: t("app_csv_scanner_error_line"),
      width: "30vw",
      cell: (row: DeliveryCsvLineError) => (
        <Tooltip title={row.line} interactive>
          <p style={{ whiteSpace: "nowrap", overflowX: "scroll" }}>{row.line}</p>
        </Tooltip>
      ),
    },
  ], [t]);

  const noAreaDeliveryCount = useMemo(() => {
    if (!selectedFileDetail?.bonusOverviews) return 0;
    return selectedFileDetail.bonusOverviews
      .filter((b) => b.territoryName === "Hors Zone")
      .reduce((sum, b) => sum + b.deliveryCount, 0);
  }, [selectedFileDetail]);

  // Heat matrices axis definitions (shared with DeliveryPointsExport via app/components/HeatMatrix)
  const hourRows = useHourRows();
  const dayOfWeekCols = useDayOfWeekCols();
  const monthCols = useMonthCols();
  const activeHeatMatrices = useMemo(() => {
    if (!queryResult) return undefined;
    return (showHorsZone ? queryResult.heatMatrices : queryResult.heatMatricesInZone) as HeatMatricesData | undefined;
  }, [queryResult, showHorsZone]);
  const yearMonthRows = useYearMonthRows(activeHeatMatrices?.yearMonth);

  // Get the bonus overviews to use for the map (file-specific or aggregated)
  const currentBonusOverviews = selectedFileDetail?.bonusOverviews ?? queryResult?.aggregatedResult?.bonusOverviews;
  const territoriesGeoJson = useTerritoriesGeoJson(currentBonusOverviews);

  // Build heatmap layers for low zoom aggregated view
  const heatmapLayers = useMemo((): HeatmapLayer[] | undefined => {
    // Only use heatmap for aggregated view (no file selected), not for single file
    if (selectedFileDetail) return undefined;

    const heatmapData = queryResult?.heatmapPoints;
    if (!heatmapData) return undefined;

    const deliveryHeatmapGeoJson = heatmapPointsToGeoJson(heatmapData.deliveryPoints);
    const horsZoneHeatmapGeoJson = heatmapPointsToGeoJson(heatmapData.horsZonePoints);
    const hubsHeatmapGeoJson = heatmapPointsToGeoJson(heatmapData.hubs);

    if (!deliveryHeatmapGeoJson && !horsZoneHeatmapGeoJson && !hubsHeatmapGeoJson) return undefined;

    return [
      ...(deliveryHeatmapGeoJson ? [{ geoJson: deliveryHeatmapGeoJson, color: "blue" }] : []),
      ...(showHorsZone && horsZoneHeatmapGeoJson ? [{ geoJson: horsZoneHeatmapGeoJson, color: "orange" }] : []),
      ...(hubsHeatmapGeoJson ? [{ geoJson: hubsHeatmapGeoJson, color: "yellow" }] : []),
    ];
  }, [selectedFileDetail, queryResult?.heatmapPoints, showHorsZone]);

  // Build points layers for single file detail view (precise GeoJSON circles)
  const pointsLayers = useMemo((): GeoZoneMapPointsLayer[] | undefined => {
    if (!selectedFileDetail) return undefined;

    const deliveryPointsGeoJson = pointsToGeoJson(selectedFileDetail.deliveryPoints);
    const horsZoneGeoJson = pointsToGeoJson(selectedFileDetail.horsZonePoints);
    const hubsGeoJson = pointsToGeoJson(selectedFileDetail.hubs);

    if (!deliveryPointsGeoJson && !horsZoneGeoJson && !hubsGeoJson) return undefined;

    return [
      ...(deliveryPointsGeoJson ? [{ geoJson: deliveryPointsGeoJson, color: "#3C78D8" }] : []),
      ...(showHorsZone && horsZoneGeoJson ? [{ geoJson: horsZoneGeoJson, color: "#FF6600" }] : []),
      ...(hubsGeoJson ? [{ geoJson: hubsGeoJson, color: "#ffe100" }] : []),
    ];
  }, [selectedFileDetail, showHorsZone]);

  // Build tile URL template with current filters for Mapbox vector tile source
  const tileUrlTemplate = useMemo(() => {
    const basePath = buildTourApiUrl("/delivery-csv-analysis/tiles/{z}/{x}/{y}.mvt");
    const params = new URLSearchParams();
    filterValues.operatorIds.forEach(id => params.append("operatorIds", id));
    filterValues.carrierIds.forEach(id => params.append("carrierIds", id));
    filterValues.importerIds.forEach(id => params.append("importerIds", id));
    filterValues.territoryCodes.forEach(code => params.append("territoryCodes", code));
    const periodStart = formatPeriod(filterValues.contentPeriodStart);
    const periodEnd = formatPeriod(filterValues.contentPeriodEnd);
    if (periodStart) params.append("contentPeriodStart", periodStart);
    if (periodEnd) params.append("contentPeriodEnd", periodEnd);
    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }, [filterValues]);

  // Build vector tile layers config for aggregated view (Mapbox fetches tiles automatically)
  const vectorTileLayers = useMemo((): VectorTileLayerConfig[] | undefined => {
    if (selectedFileDetail) return undefined;
    if (!queryResult?.aggregatedResult) return undefined;

    return [{
      sourceId: "csv-analysis-points",
      tileUrl: tileUrlTemplate,
      minzoom: 13,
      maxzoom: 22,
      layers: [
        { sourceLayer: "deliveries", type: "circle", paint: {
          "circle-radius": 6,
          "circle-color": showHorsZone
            ? ["case", ["get", "horsZone"], "#FF6600", "#3C78D8"]
            : "#3C78D8",
        }, ...(!showHorsZone && { filter: ["!", ["get", "horsZone"]] }) },
        { sourceLayer: "hubs", type: "circle", paint: { "circle-radius": 6, "circle-color": "#ffe100" } },
      ],
    }];
  }, [selectedFileDetail, queryResult, tileUrlTemplate, showHorsZone]);

  // Inject auth headers into tile requests made by Mapbox
  const transformRequest = useCallback((url: string, _resourceType: string) => {
    if (url.startsWith(deliveryApiUrl)) {
      return { url, headers: { Authorization: `Bearer ${keycloakClient.instance.token}` } };
    }
    return { url };
  }, []);

  return (
    <Box className={classes.container}>
      {/* Jobs Section */}
      {jobs.length > 0 && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {t("received_data_analysis_jobs")}
          </Typography>
          <RunningJobs jobs={jobs} onCancel={handleCancelJob} onJobClick={handleJobClick} />
        </Box>
      )}

      {/* Filters Section */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t("received_data_analysis_filters")}
        </Typography>
        <Box className={classes.filtersRow}>
          <AnalysisFilters
            filterValues={filterValues}
            onFilterChange={setFilterValues}
            importers={importerOptions}
            operators={operatorOptions}
            carriers={carrierOptions}
            territories={territoryOptions}
          />
          <Button className={classes.button} onClick={handleSearch} disabled={loading} isLoading={loading}>
            {t("received_data_analysis_search")}
          </Button>
          {queryResult?.aggregatedResult && (
            <Button
              className={classes.button}
              onClick={handleExportCsv}
              disabled={exportLoading}
              isLoading={exportLoading}
            >
              {t("received_data_analysis_export_csv")}
            </Button>
          )}
        </Box>
        {exportTriggered && (
          <Typography style={{ color: "#4caf50", marginTop: "8px", fontSize: "14px" }}>
            {t("received_data_analysis_export_triggered")}
          </Typography>
        )}
      </Box>

      {/* Content Filters Active Info - shown as soon as content filters are selected */}
      {contentFiltersActive && (
        <Box className={`${classes.alertBox} ${classes.infoAlert}`}>
          <Typography>
            {t("received_data_analysis_content_filters_info")}
          </Typography>
        </Box>
      )}

      {/* No Results Message */}
      {queryResult && !queryResult.totalFilesCount && (
        <Box className={classes.alertBox}>
          <Typography>
            {t("received_data_analysis_no_results")}
          </Typography>
        </Box>
      )}

      {/* Pending Reanalysis Alert */}
      {isPendingReanalysis && queryResult && queryResult.totalFilesCount > 0 && (
        <Box className={`${classes.alertBox} ${classes.pendingAlert}`}>
          <Typography>
            {t("received_data_analysis_pending_reanalysis", {
              missing: queryResult.missingFilesCount,
              total: queryResult.totalFilesCount,
            })}
          </Typography>
          <Box className={classes.buttonRow}>
            <Button onClick={handleTriggerReanalysis} disabled={loading}>
              {t("received_data_analysis_trigger_reanalysis")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Reanalysis In Progress Alert */}
      {isReanalysisInProgress && queryResult?.reanalysisProgress && (
        <Box className={`${classes.alertBox} ${classes.inProgressAlert}`}>
          <Typography>
            {t("received_data_analysis_in_progress", {
              processed: queryResult.reanalysisProgress.processedFiles,
              total: queryResult.reanalysisProgress.totalFiles,
            })}
          </Typography>
          <Box className={classes.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={
                (queryResult.reanalysisProgress.processedFiles /
                  queryResult.reanalysisProgress.totalFiles) *
                100
              }
            />
          </Box>
        </Box>
      )}

      {/* Results Section */}
      {statusValue === DeliveryCsvAnalysisQueryStatus.READY &&
        queryResult?.aggregatedResult && (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {t("received_data_analysis_summary")}
              </Typography>
              <AnalysisSummaryCards result={queryResult.aggregatedResult} />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {t("received_data_analysis_details")}
              </Typography>
              <AnalysisResultsTable
                analyses={queryResult.aggregatedResult.analyses}
                onRowClick={handleFileClick}
                selectedFileId={selectedFileId}
              />
            </Box>

            {/* Warnings for selected file */}
            {selectedFileDetail && (
              <>
                {selectedFileDetail.lowAveragePointsAlert && (
                  <Typography className={classes.warningText}>
                    {t("app_csv_scanner_low_average_points_alert")}
                  </Typography>
                )}
                {noAreaDeliveryCount > 0 && (
                  <Typography className={classes.warningText}>
                    {t("app_csv_scanner_no_area_delivery_count", { noAreaDeliveryCount })}
                  </Typography>
                )}
              </>
            )}

            {/* Errors / warnings for selected file */}
            {selectedFileDetail?.errors && selectedFileDetail.errors.length > 0 && (
              <Box className={classes.section}>
                {selectedFileDetail.tolerateErrors && (
                  <Typography className={classes.warningText}>
                    {t("received_data_analysis_imported_with_warnings")}
                  </Typography>
                )}
                <Typography className={classes.sectionTitle}>
                  {selectedFileDetail.tolerateErrors
                    ? `${t("received_data_analysis_analysis_warnings")} (${selectedFileDetail.errors.length})`
                    : `${t("app_csv_scanner_blocking_errors")} (${selectedFileDetail.errors.length})`
                  }
                </Typography>
                <Card className={classes.card}>
                  <DataTable
                    columns={errorColumns}
                    data={selectedFileDetail.errors}
                    noHeader
                    theme="colisactiv"
                    noDataComponent={null}
                  />
                </Card>
              </Box>
            )}

            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {selectedFileDetail
                  ? `${t("received_data_analysis_bonus_overview")} - ${selectedFileDetail.fileName}`
                  : t("received_data_analysis_bonus_overview")}
              </Typography>
              <BonusOverviewTable
                bonusOverviews={
                  selectedFileDetail?.bonusOverviews ?? queryResult.aggregatedResult.bonusOverviews
                }
              />
              {territoriesGeoJson && (
                <>
                  <Box display="flex" justifyContent="flex-end" marginBottom="-15px" marginTop="5px">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showHorsZone}
                          onChange={(e) => setShowHorsZone(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={t("received_data_analysis_show_hors_zone")}
                    />
                  </Box>
                  <GeoZoneMap
                    areaGeoJson={territoriesGeoJson}
                    pointsLayers={pointsLayers}
                    heatmapLayers={heatmapLayers}
                    vectorTileLayers={vectorTileLayers}
                    transformRequest={transformRequest}
                    mapContainerClassName={classes.mapContainer}
                    initialZoom={5}
                  />
                </>
              )}
            </Box>

            {activeHeatMatrices && (
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  {t("received_data_analysis_heat_matrices")}
                </Typography>
                <Box className={classes.matricesRow}>
                  {yearMonthRows.length > 0 && (
                    <HeatMatrix
                      title={t("received_data_analysis_year_month_matrix")}
                      cells={activeHeatMatrices.yearMonth}
                      rows={yearMonthRows}
                      cols={monthCols}
                    />
                  )}
                  <HeatMatrix
                    title={t("received_data_analysis_hour_day_matrix")}
                    cells={activeHeatMatrices.hourDayOfWeek}
                    rows={hourRows}
                    cols={dayOfWeekCols}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
    </Box>
  );
};

export default withConnect(ReceivedDataAnalysisPage);
